/*
 * Based on @braintree/sanitize-url@7.1.1 with URL.canParse polyfill
 */

const invalidProtocolRegex = /^([^\w]*)(javascript|data|vbscript)/im;
const htmlEntitiesRegex = /&#(\w+)(^\w|;)?/g;
const htmlCtrlEntityRegex = /&(newline|tab);/gi;
// eslint-disable-next-line no-control-regex
const ctrlCharactersRegex = /[\u0000-\u001F\u007F-\u009F\u2000-\u200D\uFEFF]/gim;
const urlSchemeRegex = /^.+(:|&colon;)/gim;
const whitespaceEscapeCharsRegex = /(\\|%5[cC])((%(6[eE]|72|74))|[nrt])/g;
const relativeFirstCharacters = ['.', '/'];
const BLANK_URL = 'about:blank';

function isRelativeUrlWithoutProtocol(url: string): boolean {
	return !!url[0] && relativeFirstCharacters.indexOf(url[0]) > -1;
}

function decodeHtmlCharacters(str: string) {
	const removedNullByte = str.replace(ctrlCharactersRegex, '');
	return removedNullByte.replace(htmlEntitiesRegex, (_, dec) => {
		return String.fromCharCode(dec);
	});
}

function isValidUrl(url: string): boolean {
	if (typeof URL.canParse === 'undefined') {
		try {
			new URL(url);
			return true;
		} catch {
			return false;
		}
	}

	return URL.canParse(url);
}

function decodeURI(uri: string): string {
	try {
		return decodeURIComponent(uri);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
	} catch (e: unknown) {
		// Ignoring error
		// It is possible that the URI contains a `%` not associated
		// with URI/URL-encoding.
		return uri;
	}
}

export function sanitizeUrl(url?: string): string {
	if (!url) {
		return BLANK_URL;
	}

	let charsToDecode;
	let decodedUrl = decodeURI(url.trim());

	do {
		decodedUrl = decodeHtmlCharacters(decodedUrl)
			.replace(htmlCtrlEntityRegex, '')
			.replace(ctrlCharactersRegex, '')
			.replace(whitespaceEscapeCharsRegex, '')
			.trim();

		decodedUrl = decodeURI(decodedUrl);

		charsToDecode =
			decodedUrl.match(ctrlCharactersRegex) ||
			decodedUrl.match(htmlEntitiesRegex) ||
			decodedUrl.match(htmlCtrlEntityRegex) ||
			decodedUrl.match(whitespaceEscapeCharsRegex);
	} while (charsToDecode && charsToDecode.length > 0);

	const sanitizedUrl = decodedUrl;

	if (!sanitizedUrl) {
		return BLANK_URL;
	}

	if (isRelativeUrlWithoutProtocol(sanitizedUrl)) {
		return sanitizedUrl;
	}

	// Remove any leading whitespace before checking the URL scheme
	const trimmedUrl = sanitizedUrl.trimStart();
	const urlSchemeParseResults = trimmedUrl.match(urlSchemeRegex);

	if (!urlSchemeParseResults) {
		return sanitizedUrl;
	}

	const urlScheme = urlSchemeParseResults[0].toLowerCase().trim();

	if (invalidProtocolRegex.test(urlScheme)) {
		return BLANK_URL;
	}

	const backSanitized = trimmedUrl.replace(/\\/g, '/');

	// Handle special cases for mailto: and custom deep-link protocols
	if (urlScheme === 'mailto:' || urlScheme.includes('://')) {
		return backSanitized;
	}

	// For http and https URLs, perform additional validation
	if (urlScheme === 'http:' || urlScheme === 'https:') {
		if (!isValidUrl(backSanitized)) {
			return BLANK_URL;
		}

		const url = new URL(backSanitized);
		url.protocol = url.protocol.toLowerCase();
		url.hostname = url.hostname.toLowerCase();

		return url.toString();
	}

	return backSanitized;
}
